import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { collection, getDocs, doc, deleteDoc } from "firebase/firestore";
import DataTable from "react-data-table-component";
import "../App.css";
import logo from "../assets/logo.png";
import dashboardIcon from "../assets/layout-grid.png";
import appointmentsIcon from "../assets/Tick Square.png";
import logoutIcon from "../assets/logout.png";
import userIcon from "../assets/user_avatar.png";

const UsersList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersSnapshot = await getDocs(collection(db, "users"));
      const usersList = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersList);
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDeleteUser = async (userId, userEmail) => {
    const confirmMessage = `Are you sure you want to delete user "${userEmail}"?\n\nThis action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeleting(true);
    setError("");
    setSuccess("");

    try {
      // Delete from Firestore
      const userRef = doc(db, "users", userId);
      await deleteDoc(userRef);
      console.log("✅ User document deleted from Firestore");

      // Try to delete from Firebase Auth (requires admin privileges)
      // This might fail if we don't have admin access, so we'll catch and continue
      try {
        // Note: To delete from Auth, we need the user's UID, not email
        // We can't easily get this without querying Auth, so we'll skip this for now
        // If needed, you can implement this using Firebase Admin SDK on the backend
        console.log("ℹ️ Firebase Auth deletion skipped (requires admin SDK)");
      } catch (authError) {
        console.warn("⚠️ Could not delete from Firebase Auth:", authError.message);
        // Continue anyway - Firestore deletion succeeded
      }

      setSuccess(`User "${userEmail}" deleted successfully`);
      
      // Refresh the list
      await fetchUsers();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(""), 3000);
    } catch (error) {
      console.error("Error deleting user:", error);
      setError(`Failed to delete user: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("token");
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
      localStorage.removeItem("token");
      navigate("/login");
    }
  };

  const columns = [
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: "Name",
      selector: (row) => `${row.firstName || ""} ${row.lastName || ""}`.trim() || row.username || "N/A",
      sortable: true,
    },
    {
      name: "Phone",
      selector: (row) => row.phoneNumber || "N/A",
      sortable: true,
    },
    {
      name: "Patient ID",
      selector: (row) => row.patientId || "N/A",
      sortable: true,
    },
    {
      name: "Doctor",
      selector: (row) => row.doctorName || "Not assigned",
      sortable: true,
    },
    {
      name: "Verified",
      selector: (row) => (row.isVerified ? "Yes" : "No"),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button
            onClick={() => navigate(`/edit-user/${encodeURIComponent(row.id)}`)}
            title="Edit User"
            style={{
              padding: "8px 10px",
              backgroundColor: "#4285F4",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              boxShadow: "0 2px 4px rgba(66, 133, 244, 0.2)",
              width: "36px",
              height: "36px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#357ae8";
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 4px 6px rgba(66, 133, 244, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#4285F4";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 2px 4px rgba(66, 133, 244, 0.2)";
            }}
          >
            ✏️
          </button>
          <button
            onClick={() => handleDeleteUser(row.id, row.email)}
            disabled={deleting}
            title="Delete User"
            style={{
              padding: "8px 10px",
              backgroundColor: "#dc3545",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: deleting ? "not-allowed" : "pointer",
              fontSize: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              opacity: deleting ? 0.6 : 1,
              boxShadow: "0 2px 4px rgba(220, 53, 69, 0.2)",
              width: "36px",
              height: "36px",
            }}
            onMouseEnter={(e) => {
              if (!deleting) {
                e.currentTarget.style.backgroundColor = "#c82333";
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 4px 6px rgba(220, 53, 69, 0.3)";
              }
            }}
            onMouseLeave={(e) => {
              if (!deleting) {
                e.currentTarget.style.backgroundColor = "#dc3545";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(220, 53, 69, 0.2)";
              }
            }}
          >
            🗑️
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <div className="appointments-container">
      <div className="sidebar">
        <img src={logo} alt="Timely Logo" className="logo-image" />
        <div className="user-profile">
          <div className="user-info">
            <h3>Kevin Beers</h3>
            <p>Support Team</p>
          </div>
        </div>
        <nav>
          <a href="/dashboard">
            <img src={dashboardIcon} alt="Dashboard" className="menu-icon" />
            Dashboard
          </a>
          <a href="/appointments">
            <img src={appointmentsIcon} alt="Appointments" className="menu-icon" />
            Appointments
          </a>
          <a href="/users" className="active">
            <img src={userIcon} alt="Users" className="menu-icon" />
            Users
          </a>
          <a href="/doctors">
            <span className="menu-icon" style={{ fontSize: "24px" }}>👨‍⚕️</span>
            Doctors
          </a>
          <a href="/" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
            <img src={logoutIcon} alt="Log Out" className="menu-icon" />
            Log Out
          </a>
        </nav>
      </div>

      <div className="appointments-page">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h1 className="dashboard-title">Users</h1>
          <button
            onClick={() => navigate("/add-user")}
            style={{
              padding: "10px 20px",
              backgroundColor: "#4285F4",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            Add New User
          </button>
        </div>

        {error && (
          <div
            style={{
              color: "#d32f2f",
              backgroundColor: "#ffebee",
              padding: "15px",
              borderRadius: "5px",
              marginBottom: "20px",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            style={{
              color: "#2e7d32",
              backgroundColor: "#e8f5e9",
              padding: "15px",
              borderRadius: "5px",
              marginBottom: "20px",
              fontSize: "14px",
            }}
          >
            {success}
          </div>
        )}

        <div className="appointments-table">
          <DataTable
            columns={columns}
            data={users}
            pagination
            highlightOnHover
            striped
            responsive
            progressPending={loading}
            progressComponent={<div style={{ padding: "20px" }}>Loading users...</div>}
          />
        </div>
      </div>
    </div>
  );
};

export default UsersList;

